import {
    ArrowPathIcon,
    PlusCircleIcon,
    XMarkIcon,
    CheckIcon,
    PlusIcon,
  } from "@heroicons/react/24/solid";
  import { useState, useEffect } from "react";
  import { useNavigate } from "react-router-dom";
  import { TextInput, ToggleInput, CheckboxInput, DateInput, SelectInput, AccountTypeahead } from "../components/Inputs";
  import { PageHeader } from "../components/Layouts";
  import { ActiveFilter, PrimaryTable } from "../components/Tables";
  import { useDevManager, useLoaderStore, useUserStore } from "../context";
  import { useAlert, useApi } from "../hooks";
  import constants from "../utils/constants";
  import dateHelpers from "../utils/dateHelpers";
  import { formatPhoneNumber, isDev, switchSortDirection, userHasRole } from "../utils/helpers";
  import { PrimaryButton } from "../components/Buttons";
  import { Popover } from "@headlessui/react";
  
  const EMPTY_FILTERS = {
    activeOnly: false,
  };
  
  export default function Invoices() {
    const navigate = useNavigate();
    const alert = useAlert();
    const { post, fetch } = useApi();
    const { setShowLoader } = useLoaderStore();
    const { IS_DEV } = useDevManager();
  
    const [activeFilters, setActiveFilters] = useState({
      ...EMPTY_FILTERS,
      activeOnly: true,
    });
    const [stagedFilters, setStagedFilters] = useState(activeFilters);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortDirection, setSortDirection] = useState(
      constants.SORT_DIRECTIONS.ASCENDING
    );
    const [sortField, setSortField] = useState("id");
    const [displayCount, setDisplayCount] = useState(
      constants.DISPLAY_PER_PAGE ?? 25
    );
    const [data, setData] = useState({ list: [], totalCount: 0 });
    const [isLoading, setIsLoading] = useState(false);
  
    useEffect(() => {
      getData();
      
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, displayCount, activeFilters, sortDirection, sortField]);
  
    useEffect(() => {
      setStagedFilters(activeFilters);
      setCurrentPage(1);
    }, [activeFilters]);
  
    const getData = () => {
      setIsLoading(true);
      fetch("invoice/GetPaginatedInvoices", {
        pageNumber: currentPage,
        pageSize: displayCount,
        sortDirection,
        sortField,
        isExact: false,
        accountId: activeFilters.account ? activeFilters.account.value : undefined,
        ...activeFilters,
      })
        .then((res) => {
          setData(res);
        })
        .catch((err) => {
          alert("Server error", "Could not retrieve invoices", "error");
        })
        .finally(() => setIsLoading(false));
    };
  
    const handleSort = (field) => {
      if (sortField === field) {
        setSortDirection(switchSortDirection(sortDirection));
      } else {
        setSortField(field);
      }
    };
  
    return (
      <div>
        <div className="flex justify-between">
          <PageHeader title="Account invoices" />
          <PlusCircleIcon
            className="w-8 text-primaryColor hover:text-secondaryColor cursor-pointer"
            onClick={() => navigate("/systemTenants/0")}
          />
        </div>
  
        <SystemTenantFilterSection
          stagedFilters={stagedFilters}
          setStagedFilters={setStagedFilters}
          activeFilters={activeFilters}
          setActiveFilters={setActiveFilters}
        />
  
        <PrimaryTable
          isLoading={isLoading}
          totalCount={data?.totalCount}
          displayCount={displayCount}
          currentPage={currentPage}
          setDisplayCount={setDisplayCount}
          setCurrentPage={setCurrentPage}
          header={
            <PrimaryTable.Header>
              { IS_DEV &&
              (
              <PrimaryTable.HeaderColumn
                display="Id"
                onSort={() => handleSort("id")}
                isSortingBy={sortField === "id"}
                sortDirection={sortDirection}
              />)}
              { IS_DEV &&
              (
              <PrimaryTable.HeaderColumn
                display="Created On"
                onSort={() => handleSort("createdAt")}
                isSortingBy={sortField === "createdAt"}
                sortDirection={sortDirection}
              />)}
              <PrimaryTable.HeaderColumn
                display="Name"
                onSort={() => handleSort("accountName")}
                isSortingBy={sortField === "accountName"}
                sortDirection={sortDirection}
              />
              <PrimaryTable.HeaderColumn
                display="Paid"
                onSort={() => handleSort("isPaid")}
                isSortingBy={sortField === "isPaid"}
                sortDirection={sortDirection}
              />
              <PrimaryTable.HeaderColumn
                display="Billing Period Start"
                onSort={() => handleSort("dateFrom")}
                isSortingBy={sortField === "dateFrom"}
                sortDirection={sortDirection}
              />
              <PrimaryTable.HeaderColumn
                display="Billing Period End"
                onSort={() => handleSort("dateTo")}
                isSortingBy={sortField === "dateTo"}
                sortDirection={sortDirection}
              />
            </PrimaryTable.Header>
          }
        >
          {data &&
            data.items &&
            data.items.map((x, i) => (
              <PrimaryTable.Row
                key={`${x.id}-${i}`}
                index={i}
                onClick={(e) => {
                    navigate(`/invoice/${x.id}`);
                }}
              >
                {IS_DEV && (<>
                    <PrimaryTable.Cell>{x.id}</PrimaryTable.Cell>
                    </>)}
                {IS_DEV && (<>
                    <PrimaryTable.Cell>{x.createdAt}</PrimaryTable.Cell>
                    </>)}
                <PrimaryTable.Cell>
                  {x.accountName}
                </PrimaryTable.Cell>
                <PrimaryTable.Cell>
                  {x.paymentIntentId ? "Paid" : "Unpaid"}
                </PrimaryTable.Cell>
                <PrimaryTable.Cell>
                  {dateHelpers.formatDateToShortDate(x.dateFrom)}
                </PrimaryTable.Cell>
                <PrimaryTable.Cell>
                  {dateHelpers.formatDateToShortDate(x.dateTo)}
                </PrimaryTable.Cell>
              </PrimaryTable.Row>
            ))}
        </PrimaryTable>
      </div>
    );
  }
  
  const SystemTenantFilterSection = ({
    stagedFilters,
    setStagedFilters,
    activeFilters,
    setActiveFilters,
  }) => {
    const { currentUser } = useUserStore();
    const isSysAdmin = userHasRole(currentUser, constants.ROLE_IDS.SYS_ADMIN);
    return (
      <div className="relative mb-4 rounded-xs shadow-sm bg-white p-4">
        <Popover as="div" className="relative">
          {({ open }) => (
            <>
              <Popover.Button className="flex items-center text-sm bg-gray-100 px-3 py-2 rounded-sm hover:bg-gray-200 relative focus:outline-none">
                <PlusIcon className="w-4 mr-1" />
                FILTERS
              </Popover.Button>
              <Popover.Panel
                as="div"
                className="absolute grid gap-y-4 z-50 w-full sm:w-1/3 origin-bottom-right bg-white rounded-md p-4 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none mt-1"
              >
                <div className={isSysAdmin ? "" : "hidden"}>
                    <AccountTypeahead 
                        value={stagedFilters.account} 
                        onChange={(e) => {
                            setStagedFilters({
                                ...stagedFilters,
                                account: e,
                              })
                        }} 
                    />
                </div>
                <div>
                  <CheckboxInput
                    name="active-only"
                    label="Show only active tenants"
                    checked={stagedFilters.activeOnly === true}
                    onChange={() =>
                      setStagedFilters({
                        ...stagedFilters,
                        activeOnly: stagedFilters.activeOnly ? false : true,
                      })
                    }
                  />
                </div>
                <div>
                    <DateInput
                        label={"Billing Start From"}
                        value={stagedFilters.dateFromOnOrAfter}
                        onChange={(e) =>
                            setStagedFilters({
                                ...stagedFilters,
                                dateFromOnOrAfter: e.target.value,
                            })
                        }
                    />
                </div>
                <div>
                    <DateInput
                        label={"Billing Start To"}
                        value={stagedFilters.dateFromBefore}
                        onChange={(e) =>
                            setStagedFilters({
                                ...stagedFilters,
                                dateFromBefore: e.target.value,
                            })
                        }
                    />
                </div>
                <PrimaryButton
                  text="Apply filters"
                  onClick={() => setActiveFilters(stagedFilters)}
                />
              </Popover.Panel>
            </>
          )}
        </Popover>
  
        {/* Selected filters */}
        {Object.values(activeFilters).find(
          (val) => val !== undefined && val !== false
        ) ? (
          <div className="flex items-center space-x-2 pt-6 text-sm">
            <div>Selected:</div>
            <ActiveFilter
              display="Show active only"
              value={
                activeFilters.activeOnly ? (
                  <CheckIcon className="w-4 text-green-600" />
                ) : undefined
              }
              onClear={() =>
                setActiveFilters({
                  ...activeFilters,
                  activeOnly: false,
                })
              }
            />
            <ActiveFilter
              display="Account"
              value={activeFilters.account?.label}
              onClear={() =>
                setActiveFilters({
                  ...activeFilters,
                  account: undefined,
                })
              }
            />
            <ActiveFilter
              display="Billing Start From"
              value={activeFilters.dateFromOnOrAfter}
              onClear={() =>
                setActiveFilters({
                  ...activeFilters,
                  dateFromOnOrAfter: undefined,
                })
              }
            />
            <ActiveFilter
              display="Billing Start To"
              value={activeFilters.dateFromBefore}
              onClear={() =>
                setActiveFilters({
                  ...activeFilters,
                  dateFromBefore: undefined,
                })
              }
            />
  
            <div
              className="cursor-pointer bg-gray-100 px-3 py-2 text-xs hover:bg-gray-200"
              onClick={() => {
                setActiveFilters({ ...EMPTY_FILTERS });
              }}
            >
              Clear all
            </div>
          </div>
        ) : (
          <div className="pt-6 text-sm text-gray-400">No active filters</div>
        )}
      </div>
    );
  };
  